import React, { useState, useEffect, useCallback } from "react";
import { Form, Spinner } from "react-bootstrap";
import {
  BarChart3, TrendingUp, AlertTriangle, Trophy, GitCompareArrows,
  Clock, Users, Target, CheckCircle, XCircle, Activity,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { PageHeader, StatsCard, StatsGrid, EmptyState } from "../ui";
import CommonToaster from "../Common/CommonToaster";
import { analyticsTrail } from "../QuizBank/quizCenterTrail";
import "./AssessmentAnalytics.scss";

// ── Helpers ──────────────────────────────────────────────────────────────
const fmtTime = (secs) => {
  if (!secs) return "0m";
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const pctClass = (v) => {
  if (v >= 70) return "high";
  if (v >= 40) return "mid";
  return "low";
};

// ── TrendChart sub-component ─────────────────────────────────────────────
const TrendChart = ({ data }) => {
  if (!data || data.length === 0) return <div className="analytics-card__empty">No trend data yet</div>;
  const maxVal = Math.max(...data.map((d) => Math.max(d.avgScore || 0, d.passRate || 0)), 1);

  return (
    <>
      <div className="trend-chart">
        {data.map((d) => (
          <div className="trend-chart__bar-group" key={d.date} title={`${d.date}\nAvg: ${d.avgScore}% | Pass: ${d.passRate}% | ${d.attempts} attempts`}>
            <div
              className="trend-chart__bar trend-chart__bar--score"
              style={{ height: `${(d.avgScore / maxVal) * 100}%` }}
            />
            <div
              className="trend-chart__bar trend-chart__bar--pass"
              style={{ height: `${(d.passRate / maxVal) * 100}%` }}
            />
            <span className="trend-chart__label">{d.date.slice(5)}</span>
          </div>
        ))}
      </div>
      <div className="trend-legend">
        <span className="trend-legend__item"><span className="trend-legend__dot trend-legend__dot--score" /> Avg Score</span>
        <span className="trend-legend__item"><span className="trend-legend__dot trend-legend__dot--pass" /> Pass Rate</span>
      </div>
    </>
  );
};

// ── DistributionChart sub-component ──────────────────────────────────────
const DistributionChart = ({ data }) => {
  if (!data || data.length === 0) return <div className="analytics-card__empty">No data</div>;
  const maxCount = Math.max(...data.map((d) => d.count), 1);

  return (
    <div className="distribution-chart">
      {data.map((d) => (
        <div className="distribution-chart__row" key={d.label}>
          <span className="distribution-chart__label">{d.label}</span>
          <div className="distribution-chart__bar-wrap">
            <div className="distribution-chart__bar" style={{ width: `${(d.count / maxCount) * 100}%` }} />
          </div>
          <span className="distribution-chart__count">{d.count}</span>
        </div>
      ))}
    </div>
  );
};

// ── LeaderboardSection sub-component ─────────────────────────────────────
const LeaderboardSection = ({ data }) => {
  if (!data || data.length === 0) return <div className="analytics-card__empty">No leaderboard data yet</div>;

  const getRankClass = (rank) => {
    if (rank <= 3) return `rank-badge--${rank}`;
    return "rank-badge--default";
  };

  return (
    <div style={{ overflowX: "auto" }}>
      <table className="leaderboard-table">
        <thead>
          <tr>
            <th className="rank-col">#</th>
            <th>Student</th>
            <th>Avg Score</th>
            <th>Best</th>
            <th>Attempts</th>
            <th>Pass Rate</th>
          </tr>
        </thead>
        <tbody>
          {data.map((entry) => (
            <tr key={entry._id}>
              <td className="rank-col">
                <span className={`rank-badge ${getRankClass(entry.rank)}`}>{entry.rank}</span>
              </td>
              <td>
                <div className="student-name">{entry.student?.name || "Unknown"}</div>
                {entry.student?.batch?.name && (
                  <div className="batch-label">{entry.student.batch.name}</div>
                )}
              </td>
              <td>
                <span className={`pct-badge pct-badge--${pctClass(entry.avgPercentage)}`}>
                  {entry.avgPercentage}%
                </span>
              </td>
              <td>{entry.bestPercentage}%</td>
              <td>{entry.totalAttempts}</td>
              <td>{entry.passRate}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── WeakAreasSection sub-component ───────────────────────────────────────
const WeakAreasSection = ({ questions, categories }) => {
  const [tab, setTab] = useState("questions");

  return (
    <div className="weak-areas">
      <div className="weak-areas__tabs">
        <button
          type="button"
          className={`weak-areas__tab ${tab === "questions" ? "weak-areas__tab--active" : ""}`}
          onClick={() => setTab("questions")}
        >
          Weakest Questions
        </button>
        <button
          type="button"
          className={`weak-areas__tab ${tab === "categories" ? "weak-areas__tab--active" : ""}`}
          onClick={() => setTab("categories")}
        >
          By Category
        </button>
      </div>

      {tab === "questions" && (
        <div className="weak-areas__list">
          {(!questions || questions.length === 0) && (
            <div className="analytics-card__empty">No question data yet</div>
          )}
          {questions?.map((q) => (
            <div className="weak-areas__item" key={q._id}>
              <span className="weak-areas__qtext">
                {q.question?.questionText || "Unknown question"}
              </span>
              <div className="weak-areas__stat">
                <strong>{q.totalAnswered}</strong> attempts
              </div>
              <div className="weak-areas__stat">
                <strong>{q.correctCount}</strong> correct
              </div>
              <span className={`weak-areas__accuracy weak-areas__accuracy--${pctClass(q.accuracy)}`}>
                {q.accuracy}%
              </span>
            </div>
          ))}
        </div>
      )}

      {tab === "categories" && (
        <div className="weak-areas__list">
          {(!categories || categories.length === 0) && (
            <div className="analytics-card__empty">No category data yet</div>
          )}
          {categories?.map((c) => (
            <div className="weak-areas__item" key={c._id || c.categoryName}>
              <span className="weak-areas__qtext">{c.categoryName}</span>
              <div className="weak-areas__stat">
                <strong>{c.totalAnswered}</strong> answers
              </div>
              <div className="weak-areas__stat">
                <strong>{c.correctCount}</strong> correct
              </div>
              <span className={`weak-areas__accuracy weak-areas__accuracy--${pctClass(c.accuracy)}`}>
                {c.accuracy}%
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── BatchComparison sub-component ────────────────────────────────────────
const BatchComparison = ({ data }) => {
  if (!data || data.length === 0) return <div className="analytics-card__empty">No batch data yet</div>;
  const maxPct = Math.max(...data.map((d) => d.avgPercentage), 1);

  return (
    <div className="batch-comparison">
      {data.map((b) => (
        <div className="batch-comparison__item" key={b.batchId}>
          <div>
            <div className="batch-comparison__name">{b.batchName}</div>
            <div className="batch-comparison__meta">
              {b.participatingStudents}/{b.studentCount} students · {b.totalAttempts} attempts · {b.passRate}% pass
            </div>
          </div>
          <div className="batch-comparison__bar-wrap">
            <div
              className={`batch-comparison__bar batch-comparison__bar--${pctClass(b.avgPercentage)}`}
              style={{ width: `${(b.avgPercentage / maxPct) * 100}%` }}
            />
          </div>
          <div className="batch-comparison__pct">{b.avgPercentage}%</div>
        </div>
      ))}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════════════════
const AnalyticsDashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // Filters
  const [period, setPeriod] = useState("30d");
  const [assessmentId, setAssessmentId] = useState("");
  const [assessments, setAssessments] = useState([]);

  // Data
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState([]);
  const [weakData, setWeakData] = useState({ weakQuestions: [], weakCategories: [] });
  const [leaderboard, setLeaderboard] = useState([]);
  const [batchComparison, setBatchComparison] = useState([]);
  const [assessmentDetail, setAssessmentDetail] = useState(null);

  // Fetch assessment list for filter dropdown
  useEffect(() => {
    apiClient.get("/assessments?limit=200&status=published")
      .then(({ data }) => setAssessments(data.assessments || []))
      .catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (assessmentId) params.assessmentId = assessmentId;

      const [overviewRes, trendsRes, weakRes, lbRes, batchRes] = await Promise.all([
        apiClient.get("/assessment-analytics/overview"),
        apiClient.get("/assessment-analytics/trends", { params: { period, ...params } }),
        apiClient.get("/assessment-analytics/weak-areas", { params }),
        apiClient.get("/assessment-analytics/leaderboard", { params: { ...params, limit: 20 } }),
        apiClient.get("/assessment-analytics/batch-comparison", { params }),
      ]);

      setOverview(overviewRes.data);
      setTrends(trendsRes.data.trends || []);
      setWeakData(weakRes.data);
      setLeaderboard(lbRes.data.leaderboard || []);
      setBatchComparison(batchRes.data.comparison || []);

      // Fetch assessment-specific detail if one is selected
      if (assessmentId) {
        const detailRes = await apiClient.get(`/assessment-analytics/assessment/${assessmentId}`);
        setAssessmentDetail(detailRes.data);
      } else {
        setAssessmentDetail(null);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to load analytics";
      setToast({ show: true, message: msg, type: "error" });
    }
    setLoading(false);
  }, [period, assessmentId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const perf = overview?.performance || {};

  return (
    <div className="analytics-dashboard">
      <PageHeader
        icon={BarChart3}
        title="Assessment Analytics"
        description="Performance trends, weak areas, leaderboards and batch comparison"
        breadcrumbs={analyticsTrail()}
      />

      {/* Toolbar / Filters */}
      <div className="analytics-toolbar">
        <div className="filter-wrapper">
          <Form.Select size="sm" value={period} onChange={(e) => setPeriod(e.target.value)}>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </Form.Select>
        </div>
        <div className="filter-wrapper" style={{ minWidth: 220 }}>
          <Form.Select size="sm" value={assessmentId} onChange={(e) => setAssessmentId(e.target.value)}>
            <option value="">All Assessments</option>
            {assessments.map((a) => (
              <option key={a._id} value={a._id}>{a.title}</option>
            ))}
          </Form.Select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" /></div>
      ) : (
        <>
          {/* Overview Stats */}
          <StatsGrid cols={6} className="analytics-stats-row">
            <StatsCard
              icon={Activity}
              iconColor="primary"
              title="Total Attempts"
              value={perf.totalAttempts || 0}
            />
            <StatsCard
              icon={Target}
              iconColor="success"
              title="Avg Score"
              value={`${perf.avgPercentage || 0}%`}
            />
            <StatsCard
              icon={CheckCircle}
              iconColor="success"
              title="Passed"
              value={perf.passCount || 0}
            />
            <StatsCard
              icon={XCircle}
              iconColor="danger"
              title="Failed"
              value={perf.failCount || 0}
            />
            <StatsCard
              icon={Users}
              iconColor="info"
              title="Students"
              value={perf.uniqueStudents || 0}
            />
            <StatsCard
              icon={Clock}
              iconColor="warning"
              title="Avg Time"
              value={fmtTime(perf.avgTimeSpent || 0)}
            />
          </StatsGrid>

          {/* Row: Trends + Distribution */}
          <div className="d-flex gap-3 flex-wrap">
            <div className="analytics-card flex-fill" style={{ minWidth: 320 }}>
              <div className="analytics-card__title">
                <TrendingUp size={16} /> Performance Trends
              </div>
              <TrendChart data={trends} />
            </div>

            {assessmentDetail && (
              <div className="analytics-card" style={{ minWidth: 280, maxWidth: 360 }}>
                <div className="analytics-card__title">
                  <BarChart3 size={16} /> Score Distribution
                </div>
                <DistributionChart data={assessmentDetail.scoreDistribution} />
              </div>
            )}
          </div>

          {/* Weak Areas */}
          <div className="analytics-card">
            <div className="analytics-card__title">
              <AlertTriangle size={16} /> Weak Areas
            </div>
            <WeakAreasSection
              questions={weakData.weakQuestions}
              categories={weakData.weakCategories}
            />
          </div>

          {/* Row: Leaderboard + Batch Comparison */}
          <div className="d-flex gap-3 flex-wrap">
            <div className="analytics-card flex-fill" style={{ minWidth: 400 }}>
              <div className="analytics-card__title">
                <Trophy size={16} /> Leaderboard
              </div>
              <LeaderboardSection data={leaderboard} />
            </div>

            <div className="analytics-card flex-fill" style={{ minWidth: 350 }}>
              <div className="analytics-card__title">
                <GitCompareArrows size={16} /> Batch Comparison
              </div>
              <BatchComparison data={batchComparison} />
            </div>
          </div>

          {/* Recent Attempts */}
          {overview?.recentAttempts?.length > 0 && (
            <div className="analytics-card">
              <div className="analytics-card__title">
                <Clock size={16} /> Recent Attempts
              </div>
              <div style={{ overflowX: "auto" }}>
                <table className="recent-attempts-table">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Assessment</th>
                      <th>Score</th>
                      <th>Result</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.recentAttempts.map((a) => (
                      <tr key={a._id}>
                        <td>{a.student?.name || "—"}</td>
                        <td>{a.assessment?.title || "—"}</td>
                        <td>{a.score}/{a.totalMarks} ({a.percentage}%)</td>
                        <td>
                          <span className={`pass-badge pass-badge--${a.passed ? "pass" : "fail"}`}>
                            {a.passed ? "Passed" : "Failed"}
                          </span>
                        </td>
                        <td>{a.submittedAt ? new Date(a.submittedAt).toLocaleDateString() : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Empty State */}
          {perf.totalAttempts === 0 && (
            <EmptyState
              icon={<BarChart3 size={40} />}
              title="No Analytics Data Yet"
              description="Analytics will appear once students start taking assessments"
            />
          )}
        </>
      )}

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />
    </div>
  );
};

export default AnalyticsDashboardPage;
